import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { listAPI, categoryAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";
import ReactMarkdown from "react-markdown";

const Home = () => {
  const [lists, setLists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [randomizing, setRandomizing] = useState(false);
  const [sortBy, setSortBy] = useState("recent");
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("search");
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 21; // 21 items per page (3x7 grid)

  usePageTitle(
    searchQuery ? `Search: ${searchQuery}` : "Guess. Reveal. Complete.",
  );

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(0); // Reset to first page when sort or search changes
    loadLists(0);
  }, [sortBy, searchQuery]);

  useEffect(() => {
    loadLists(currentPage);
  }, [currentPage]);

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadLists = async (page) => {
    setLoading(true);
    try {
      let response;
      if (searchQuery) {
        response = await listAPI.searchLists(searchQuery, page, pageSize);
      } else {
        response = await listAPI.getPublicLists(page, pageSize, sortBy);
      }
      setLists(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements);
      setCurrentPage(response.data.number);

      // Scroll to top smoothly when page changes
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Failed to load lists:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRandomList = async () => {
    setRandomizing(true);
    try {
      const response = await listAPI.getRandomList();
      navigate(`/list/${response.data.slug}`);
    } catch (error) {
      console.error("Failed to get random list:", error);
      setRandomizing(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, current page, and nearby pages
      if (currentPage <= 2) {
        // Near the start
        for (let i = 0; i < 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages - 1);
      } else if (currentPage >= totalPages - 3) {
        // Near the end
        pages.push(0);
        pages.push("...");
        for (let i = totalPages - 4; i < totalPages; i++) pages.push(i);
      } else {
        // In the middle
        pages.push(0);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
          {searchQuery
            ? `Search Results for "${searchQuery}"`
            : "Explore Lists"}
        </h1>
        <p className="text-gray-400 text-sm sm:text-base">
          Choose a list and test your knowledge by guessing all the items!
        </p>
      </div>

      {/* Random List Button - Subtle Professional Style */}
      {!searchQuery && (
        <button
          onClick={handleRandomList}
          disabled={randomizing}
          className="w-full mb-8 bg-dark-card hover:bg-blue-600/10 border-2 border-blue-600 hover:border-blue-500 text-white rounded-lg p-4 sm:p-5 transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl group relative overflow-hidden"
        >
          {/* Subtle glow effect on hover */}
          <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

          <div className="relative flex items-center justify-center gap-4">
            {/* Icon */}
            {randomizing ? (
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            ) : (
              <svg
                className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500 group-hover:rotate-12 group-hover:scale-110 transition-all duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            )}

            {/* Text */}
            <div className="text-center">
              <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                {randomizing ? "Finding your list..." : "Discover Random List"}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm">
                {randomizing
                  ? "Just a moment..."
                  : "Try something new and exciting"}
              </div>
            </div>

            {/* Arrow */}
            {!randomizing && (
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            )}
          </div>
        </button>
      )}

      {/* Categories */}
      {!searchQuery && (
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-3 sm:mb-4">
            Categories
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="bg-dark-card hover:bg-dark-hover border border-gray-700 p-3 sm:p-4 rounded-lg text-center transition active:scale-95"
              >
                <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                  {category.name}
                </h3>
                <p className="text-gray-400 text-xs sm:text-sm">
                  {category.listCount}{" "}
                  {category.listCount === 1 ? "list" : "lists"}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Sort Options and Results Count */}
      {!searchQuery && (
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-center sm:items-center gap-3">
          <div className="text-center sm:text-left">
            <h2 className="text-lg sm:text-xl font-semibold text-white">
              All Lists
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {totalElements} {totalElements === 1 ? "list" : "lists"} available
            </p>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 text-sm sm:text-base bg-dark-card border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 w-full sm:w-auto"
          >
            <option value="recent">Recently Added</option>
            <option value="popularity">Most Popular</option>
            <option value="most-items">Most Items</option>
            <option value="fewest-items">Fewest Items</option>
            <option value="az">A → Z</option>
            <option value="za">Z → A</option>
          </select>
        </div>
      )}

      {/* Search Results Count */}
      {searchQuery && (
        <div className="mb-4">
          <p className="text-sm text-gray-400">
            Found {totalElements} {totalElements === 1 ? "result" : "results"}
          </p>
        </div>
      )}

      {/* Lists Grid */}
      {loading ? (
        <div className="text-center text-gray-400 py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-4">Loading...</p>
        </div>
      ) : lists.length === 0 ? (
        <div className="text-center text-gray-400 py-12 bg-dark-card border border-gray-700 rounded-lg">
          <p className="text-lg">
            {searchQuery
              ? "No lists found for your search."
              : "No lists available."}
          </p>
          {searchQuery && (
            <button
              onClick={() => (window.location.href = "/")}
              className="mt-4 text-blue-400 hover:text-blue-300 underline"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {lists.map((list) => (
              <Link
                key={list.id}
                to={`/list/${list.slug}`}
                className="bg-dark-card hover:bg-dark-hover border border-gray-700 p-4 sm:p-6 rounded-lg transition group active:scale-98"
              >
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition line-clamp-2">
                  {list.title}
                </h3>

                {list.description && (
                  <div className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 prose prose-invert prose-sm">
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <span {...props} />,
                        strong: ({ node, ...props }) => (
                          <strong className="text-white" {...props} />
                        ),
                        em: ({ node, ...props }) => <em {...props} />,
                      }}
                    >
                      {list.description}
                    </ReactMarkdown>
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  {list.categories
                    .filter((cat) => cat.name.toLowerCase() !== "ranked")
                    .map((cat) => (
                      <span
                        key={cat.id}
                        className="px-2 py-0.5 sm:py-1 bg-blue-600/20 text-blue-400 text-xs rounded whitespace-nowrap"
                      >
                        {cat.name}
                      </span>
                    ))}
                  {list.isRanked && (
                    <span className="px-2 py-0.5 sm:py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded whitespace-nowrap">
                      Ranked
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center text-xs sm:text-sm text-gray-400 mb-2">
                  <span className="font-medium">{list.totalItems} items</span>
                  <div className="flex items-center gap-1.5">
                    <svg
                      className="w-3 h-3 sm:w-4 sm:h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    <span>
                      {list.popularity}{" "}
                      {list.popularity === 1 ? "play" : "plays"}
                    </span>
                  </div>
                </div>

                {/* Progress Indicator */}
                {list.isCompleted && (
                  <div className="flex items-center gap-1.5 text-green-400 text-xs sm:text-sm font-medium">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>Completed</span>
                  </div>
                )}
                {!list.isCompleted && list.isStarted && (
                  <div className="flex items-center gap-1.5 text-orange-400 text-xs sm:text-sm font-medium">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      In Progress ({list.guessedCount}/{list.totalItems})
                    </span>
                  </div>
                )}
              </Link>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-dark-card border border-gray-700 rounded-lg p-4">
              {/* Page Info */}
              <div className="text-sm text-gray-400">
                Page {currentPage + 1} of {totalPages}
              </div>

              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                {/* Previous Button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-2 bg-dark-hover border border-gray-600 rounded-lg text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-700 hover:border-blue-500 transition text-sm font-medium"
                  aria-label="Previous page"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Page Numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {getPageNumbers().map((pageNum, index) =>
                    pageNum === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-3 py-2 text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white border border-blue-500"
                            : "bg-dark-hover border border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-blue-500"
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    ),
                  )}
                </div>

                {/* Mobile: Current Page Only */}
                <div className="sm:hidden px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium border border-blue-500">
                  {currentPage + 1}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                  className="px-3 py-2 bg-dark-hover border border-gray-600 rounded-lg text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-700 hover:border-blue-500 transition text-sm font-medium"
                  aria-label="Next page"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              {/* Quick Jump (Desktop only) */}
              <div className="hidden lg:flex items-center gap-2">
                <label htmlFor="pageJump" className="text-sm text-gray-400">
                  Go to:
                </label>
                <select
                  id="pageJump"
                  value={currentPage}
                  onChange={(e) => handlePageChange(parseInt(e.target.value))}
                  className="px-3 py-2 bg-dark-hover border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 text-sm"
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i} value={i}>
                      Page {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Home;
